import React, { FC, useMemo, useState } from 'react';

import { TimeZoneEnums } from '@nhl/nhl-breakaway-ui';

import { CountryCode } from '~/core/enums';
import { BettingOddPartner } from '~/core/types';
import { compareGamesByState } from '~/core/utils';
import OddsProvider from '~/views/components/oddsProvider/OddsProvider';

import { ScoresGame } from '../../types';
import GameCard from '../gameCard/GameCard';
import GameStories from '../gameStories/GameStories';

import { DayContainer, HeaderContainer, OddsAndStoriesContainer } from './styles';

type OddDetails = {
    bettingOddPartner?: BettingOddPartner;
    bettingOddPartnerIndex: number;
    oddsExist: boolean;
};

type DayProps = {
    games: Array<ScoresGame>;
    date: string;
    timezone: TimeZoneEnums | string;
    countryCode: CountryCode;
    oddsPartners?: Array<BettingOddPartner>;
};

const getOddDetails = (
    countryCode: CountryCode,
    games: Array<ScoresGame>,
    oddsPartners?: Array<BettingOddPartner>
): OddDetails => {
    if (!oddsPartners) {
        // Like in a JS findIndex, if the index is not found we will return -1
        return {
            bettingOddPartnerIndex: -1,
            oddsExist: false,
        };
    }

    const bettingOddPartnerIndex = oddsPartners.findIndex(
        (partner: BettingOddPartner) => partner.country === countryCode.toUpperCase()
    );

    const bettingOddPartner = oddsPartners[bettingOddPartnerIndex];

    const gameOddsExist =
        (bettingOddPartner &&
            games.find((game) => {
                return (
                    game.homeTeam.odds &&
                    game.homeTeam.odds.find((odd) => odd.providerId === bettingOddPartner?.partnerId)
                );
            }) !== undefined) ||
        false;

    return {
        bettingOddPartner,
        bettingOddPartnerIndex,
        oddsExist: !!bettingOddPartner && gameOddsExist,
    };
};

const Day: FC<DayProps> = ({ games, date, timezone, countryCode, oddsPartners }) => {
    const [hideBettingOdds, setHideBettingOdds] = useState(false);

    const oddDetails = useMemo<OddDetails>(
        () => getOddDetails(countryCode, games, oddsPartners),
        [games, countryCode, oddsPartners]
    );

    return (
        <>
            <HeaderContainer>
                <h3 aria-level={2}>{date}</h3>
            </HeaderContainer>
            <OddsAndStoriesContainer>
                <GameStories games={games} countryCode={countryCode} />
                {oddDetails.oddsExist && oddDetails.bettingOddPartner && (
                    <OddsProvider
                        bettingOddPartner={oddDetails.bettingOddPartner}
                        showToggle
                        hideBettingOdds={hideBettingOdds}
                        toggleBettingOdds={() => setHideBettingOdds(!hideBettingOdds)}
                    />
                )}
            </OddsAndStoriesContainer>
            <DayContainer>
                {games.toSorted(compareGamesByState).map((game) => (
                    <GameCard
                        key={game.id}
                        game={game}
                        hideOdds={hideBettingOdds}
                        timezone={timezone}
                        countryCode={countryCode}
                        partnerId={oddDetails.bettingOddPartner?.partnerId}
                    />
                ))}
            </DayContainer>
        </>
    );
};

export default Day;
